import React from 'react';
import styled from 'styled-components';

import { LabeledDateTime } from './flightDataUtils';
import {
  parseLuxonDateToParts,
  emptyDateFieldCharacter,
  ParsedTime,
  defaultParsedTime,
} from '../../lib/date';

interface DateTimeDisplayProps {
  dateTimeData: LabeledDateTime;
}

interface PaneProps {
  aspect: string;
  size?: string;
}

const Pane = styled.div<PaneProps>`
  flex-basis: ${props => {
    const is21or42 = ['2:1', '4:2'].includes(props.aspect);
    const is41 = props.aspect === '4:1';
    return is21or42 ? '49%' : is41 ? '24%' : '99%';
  }};
  flex-direction: column;
  color: #ffffff;
  font-family: Arial, Helvetica, sans-serif;
  padding-bottom: 15px;
  ${props => (props.size === 'half' ? 'min-width: 320px;\nmax-width: 500px' : '')}
`;

const LargeBold = styled.div`
  font-weight: 400;
  font-size: 22px;
  padding: 10px 10px;
`;

const SmallBold = styled.div`
  font-size: 12px;
  font-weight: bold;
`;

const SmallNonBoldDiv = styled.div`
  font-size: 10px;
  font-weight: normal;
`;

const SmallNonBoldSpan = styled.span`
  font-size: 10px;
  font-weight: normal;
  margin: 0px 2px;
`;

/**
 * DateTimeDisplay component to display date and time information.
 * @param {DateTimeDisplayProps} props - The props for the component.
 * @returns {JSX.Element} The DateTimeDisplay component.
 */
export const DateTimeDisplay: React.FC<DateTimeDisplayProps> = ({ dateTimeData }) => {
  const timeParts: ParsedTime = dateTimeData?.dateTime ? parseLuxonDateToParts(dateTimeData.dateTime) : defaultParsedTime;
  return (
    <Pane aspect="2:1">
      <SmallBold>{dateTimeData.label}</SmallBold>
      <LargeBold>
        {timeParts.time12Hr}
        {timeParts.time12Hr !== emptyDateFieldCharacter && (
          <>
            <SmallNonBoldSpan>{timeParts.dayPeriod}</SmallNonBoldSpan>
            <SmallNonBoldSpan>{timeParts.timeZoneShortName}</SmallNonBoldSpan>
          </>
        )}
      </LargeBold>
      <SmallNonBoldDiv>{timeParts.displayDateFormat}</SmallNonBoldDiv>
    </Pane>
  );
};